/**
 * Forward a customer's message to one or more people with a note: pick from the Setup list, type any address,
 * or both. Addresses outside Air Age are flagged before sending. The forward is recorded as an internal note.
 */
import { useState, type KeyboardEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { apiErrorText } from '../../lib/apiErrorText'
import { isInternalEmail, parseAddresses, type DeskChannel, type DeskNote, type ForwardAddress } from '../../lib/supportDesk'

const MAX_RECIPIENTS = 20
const BUTTON = 'label border border-ink-500 px-3 py-2 hover:border-amber-glow disabled:opacity-40'

type Props = { channel: DeskChannel; id: number; publication: string | null; onDone: () => void }

export function ForwardPanel({ channel, id, publication, onDone }: Props) {
  const qc = useQueryClient()
  const saved = useQuery<ForwardAddress[]>({
    queryKey: ['support', 'forward-addresses', publication ?? ''],
    queryFn: () => api.get<ForwardAddress[]>(`/api/support/forward-addresses${publication ? `?publication=${publication}` : ''}`),
  })
  const [to, setTo] = useState<string[]>([])
  const [typed, setTyped] = useState('')
  const [bad, setBad] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [includeMember, setIncludeMember] = useState(false)

  const send = useMutation({
    mutationFn: () => api.post<DeskNote>(`/api/support/conversations/${channel}/${id}/forward`,
      { to, note, include_member: includeMember }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['support'] }); onDone() },
  })

  const add = (addresses: string[]) => setTo(prev => [...prev, ...addresses.filter(a => !prev.includes(a))].slice(0, MAX_RECIPIENTS))
  const toggle = (email: string) => setTo(prev => (prev.includes(email) ? prev.filter(a => a !== email) : [...prev, email].slice(0, MAX_RECIPIENTS)))
  const commitTyped = () => {
    const { valid, invalid } = parseAddresses(typed)
    add(valid)
    setBad(invalid)
    setTyped(invalid.join(' '))
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); commitTyped() }
  }
  const outside = to.filter(a => !isInternalEmail(a))
  const ready = to.length > 0 && note.trim().length > 0 && !send.isPending

  return (
    <section aria-label="Forward" className="border border-ink-500 bg-ink-800/60 p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="label-amber">Forward this message</h3>
        <button type="button" className="label hover:text-paper" onClick={onDone}>Cancel</button>
      </div>

      {(saved.data ?? []).length > 0 && (
        <div role="group" aria-label="Saved addresses" className="flex flex-wrap gap-1.5">
          {saved.data!.map(a => {
            const on = to.includes(a.email)
            return (
              <button key={a.id} type="button" aria-pressed={on} onClick={() => toggle(a.email)} title={a.email}
                className={`font-mono text-[11px] px-2 py-1 border rounded-sm ${on ? 'border-amber-glow text-amber-glow bg-amber-glow/10' : 'border-ink-500 text-paper-dim hover:text-paper'}`}>
                {on ? '✓ ' : '+ '}{a.label}
              </button>
            )
          })}
        </div>
      )}
      {saved.isSuccess && saved.data.length === 0 && (
        <p className="text-paper-trace text-xs">No saved addresses yet. An admin can add them in Customer Support → Setup.</p>
      )}

      <div>
        <label htmlFor="fwd-typed" className="label block mb-1">Other addresses</label>
        <div className="flex gap-2">
          <input id="fwd-typed" value={typed} onChange={e => setTyped(e.target.value)} onKeyDown={onKey} onBlur={() => typed.trim() && commitTyped()}
            placeholder="name@example.com — press Enter to add" autoComplete="off"
            className="flex-1 bg-ink-900 border border-ink-500 px-2 py-2 text-paper font-mono text-[12px]" />
          <button type="button" className={BUTTON} disabled={!typed.trim()} onClick={commitTyped}>Add</button>
        </div>
        {bad.length > 0 && <p role="alert" className="text-red-400 text-xs mt-1">Not an email address: {bad.join(', ')}</p>}
      </div>

      <div>
        <div className="label mb-1">To ({to.length}{to.length >= MAX_RECIPIENTS ? `, the most at once` : ''})</div>
        {to.length === 0 ? <p className="text-paper-trace text-sm">Nobody yet.</p> : (
          <ul className="flex flex-wrap gap-1.5">
            {to.map(a => {
              const out = !isInternalEmail(a)
              return (
                <li key={a} className={`flex items-center gap-1 font-mono text-[12px] px-2 py-1 border rounded-sm ${out ? 'border-amber-300/70 text-amber-300' : 'border-ink-500 text-paper'}`}>
                  {a}{out && <span className="text-[10px] uppercase">· outside</span>}
                  <button type="button" aria-label={`Remove ${a}`} onClick={() => toggle(a)} className="ml-1 text-paper-dim hover:text-paper">×</button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div>
        <label htmlFor="fwd-note" className="label block mb-1">Your note</label>
        <textarea id="fwd-note" value={note} onChange={e => setNote(e.target.value)} rows={4} maxLength={4000}
          placeholder="What you need from them"
          className="w-full bg-ink-900 border border-ink-500 px-2 py-2 text-paper text-[14px]" />
      </div>

      <label className="flex items-center gap-2 text-sm text-paper-dim">
        <input type="checkbox" checked={includeMember} onChange={e => setIncludeMember(e.target.checked)} />
        Include account details (plan, status, renewal date, who bills it)
      </label>
      <p className="text-paper-trace text-xs">
        The customer's message{channel === 'email' ? ' and its attachments (up to 20 MB) go' : ' goes'} below your note.
        Replies come back to you, not to the customer. The forward is saved as an internal note.
      </p>

      {outside.length > 0 && (
        <p className="text-amber-300 text-sm">
          {outside.length === 1 ? '1 address is' : `${outside.length} addresses are`} outside Air Age: {outside.join(', ')}. Check before sending.
        </p>
      )}
      {send.isError && <p role="alert" className="text-red-400 text-sm">{apiErrorText(send.error)}</p>}
      <button type="button" className="label-amber border border-amber-glow px-4 py-2 hover:bg-amber-glow/10 disabled:opacity-40"
        disabled={!ready} onClick={() => send.mutate()}>
        {send.isPending ? 'Sending…' : `Forward to ${to.length || '…'}`}
      </button>
    </section>
  )
}
